import { type FC, useState, useCallback } from 'react';
import styled, { useTheme } from 'styled-components';
import { ValidateOTPCard } from './components/ValidateOTPCard';
import { useZustandStore } from '../../appState/StoresProvider';
import { useOtpRequestExpiryHandling } from './hooks/useOtpRequestExpiryHandling';
import { Spinner } from '../Spinner/Spinner';
import { useAuthStore } from '../Login/useAuthStore';
import { otpCodeStillUsable, otpSignInErrorMessage } from './otpSignInError';

export const OneTimePassword: FC = () => {
  const resetOtpRequestInfo = useAuthStore((state) => state.resetOtpRequestInfo);
  const requestOTP = useAuthStore((state) => state.requestOTP);
  const signInWithOTP = useAuthStore((state) => state.signInWithOTP);
  const waiting = useZustandStore('auth', (v) => v.isFetching);
  const [error, setError] = useState<string | undefined>(undefined);
  // Set once a failure has ended the Cognito session, so the code on screen can no longer be
  // submitted. A wrong answer with attempts left does not end it.
  const [codeUsedUp, setCodeUsedUp] = useState(false);

  const otpRequestInfo = useOtpRequestInfo();
  const { normalizedTimeToExpiry, canResend } = useOtpRequestExpiryHandling();

  const handleBack = useCallback(() => {
    resetOtpRequestInfo();
  }, [resetOtpRequestInfo]);

  const handleResend = useCallback(() => {
    if (otpRequestInfo == null) {
      return;
    }
    setError(undefined);
    requestOTP(otpRequestInfo.username)
      .then(() => setCodeUsedUp(false))
      .catch(() => {
        setError("We couldn't send a new code. Go back and try again.");
      });
  }, [otpRequestInfo, requestOTP]);

  const handeSubmit = useCallback(
    (code: string) => {
      // Re-submitting against the ended session can only fail, with a misleading
      // "Invalid session" (HRI2-18976); the learner needs a new code instead.
      if (codeUsedUp) {
        return;
      }
      setError(undefined);

      /*
       * The rejection has to be handled here. Without a catch, a failed sign-in only reached the
       * console and the card sat there with no message and no way forward - which is what a
       * learner on the wrong tenant's URL saw, even though the code they typed was correct.
       */
      signInWithOTP(code).catch((caught: unknown) => {
        setCodeUsedUp(!otpCodeStillUsable(caught));
        setError(otpSignInErrorMessage(caught));
      });
    },
    [codeUsedUp, signInWithOTP],
  );
  const theme = useTheme();

  return (
    <OTPContainer>
      {otpRequestInfo ? (
        <ValidateOTPCard
          email={otpRequestInfo.username}
          normalizedTimeToExpiry={normalizedTimeToExpiry}
          // Resend is normally held back for 20 seconds, but after a failed attempt it is the only
          // way forward, so it is offered straight away.
          canResend={canResend || codeUsedUp}
          handleBack={handleBack}
          handleResend={handleResend}
          handleSubmit={handeSubmit}
          waiting={waiting}
          errorText={error}
          buttonDisabled={codeUsedUp}
        />
      ) : (
        <Spinner color={theme.colors.prototype.primary} size="48px" />
      )}
    </OTPContainer>
  );
};

const OTPContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100%;
  background: ${({ theme }) => theme.colors.prototype.background};
`;
function useOtpRequestInfo() {
  const otpRequestInfo = useAuthStore((state) => state.otpRequestInfo);

  // if (otpRequestInfo == null) {
  //   throw Error('No OTP request info available. This is unexpected');
  // }
  return otpRequestInfo;
}
